import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { withStyles, Typography, withWidth } from '@material-ui/core';
import { withRouter } from 'react-router-dom';
import {
    PaymentElement,
    useStripe,
    useElements
} from '@stripe/react-stripe-js';
import axios from 'axios';
import { red } from '@material-ui/core/colors';
import { Auth } from 'aws-amplify';
import LoaderButton from './LoaderButton';
import { formatPrice } from '../helpers/formatters';
import { connectionInfo, batteryInfo } from '../helpers/browser';
import {
    DEFAULT_ERROR,
    getFriendlyPaymentError,
    isSafePaymentError
} from '../helpers/errors';
import { isAdhoc, calculateSelectPrice } from '../helpers/ticket';

const styles = {
    root: {
        marginTop: '20px',
        minHeight: '100px',
        marginBottom: '20px',
        padding: '0 10px 10px',
        textAlign: 'center'
    },
    button: {
        margin: '20px 0 20px',
        width: '200px',
        height: '56px'
    },
    error: {
        margin: '20px 20px 10px',
        color: red[500],
        minHeight: '20px'
    },
    spinner: {
        marginTop: 60,
        marginBottom: 60,
        marginLeft: 'calc(50% - 22px)'
    },
    info: {
        margin: '20px auto',
        maxWidth: '500px',
        padding: '15px',
        background: '#f5f5f5',
        borderRadius: '4px'
    },
    paymentElement: {
        margin: '30px auto 15px auto',
        maxWidth: '500px'
    }
};

const BankPaymentForm = props => {
    const {
        classes,
        history,
        ticket,
        service,
        passenger,
        client,
        voucher,
        buyAgain,
        disablePhoto,
        paymentIntent,
        setAllowEdit
    } = props;

    const stripe = useStripe();
    const elements = useElements();

    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState('');
    const [paymentError, setPaymentError] = useState(false);
    const [paymentReady, setPaymentReady] = useState(false);
    const [clientSideFlowStarted, setClientSideFlowStarted] = useState(false);
    const [encryptedTicketId, setEncryptedTicketId] = useState('');

    const strippedStripeAccountId = client.stripe_account_id
        ? client.stripe_account_id.replace('acct_', '')
        : '';

    const uploadImage = async () => {
        let response;

        const startTime = Date.now();

        try {
            const currentSession = await Auth.currentSession();

            response = await axios.post(
                `${process.env.REACT_APP_API_ENDPOINT}/customer-ticket/pre-signed-url`,
                {
                    fileType: passenger.photo.type,
                    fileSize: passenger.photo.size,
                    user: currentSession.getAccessToken().payload.sub
                }
            );
        } catch (err) {
            err.source = 'pre-signed-url';
            throw err;
        }

        const startUploadTime = Date.now();

        try {
            // @NOTE: Skip photo upload for automated tests
            // This will also break photos for local dev.
            // @TODO: Fix so skipped for tests but available for local dev
            if (process.env.NODE_ENV !== 'development') {
                await axios.put(response.data.preSignedUrl, passenger.photo, {
                    headers: {
                        'Content-Type': passenger.photo.type
                    },
                    withCredentials: false,
                    transformRequest: v => v
                });
            }
        } catch (err) {
            err.source = 's3.put';
            err.timing = {
                preSignedUrlTime: startUploadTime - startTime,
                uploadTime: Date.now() - startUploadTime
            };
            throw err;
        }

        return response.data.fileName;
    };

    const netPrice = () => {
        const useZones =
            ticket.pricing_model === 'Zonal' &&
            ticket.zone_prices &&
            passenger.zone;

        let ticketPrice;

        if (ticket.sub_type === 'select') {
            ticketPrice = calculateSelectPrice(
                passenger.travelPattern,
                ticket,
                passenger.zone
            );
        } else {
            ticketPrice = useZones
                ? ticket.zone_prices[passenger.zone]
                : ticket.price;
        }

        if (voucher) {
            return ticketPrice - voucher.value;
        }

        return ticketPrice;
    };

    const confirmPayment = async encryptedId => {
        const returnUrl = '/bank-order-confirmed';
        const queryParams = new URLSearchParams({
            csa: strippedStripeAccountId,
            encrypted_ctid: encryptedId,
            year: ticket.year.replace('/', '-'),
            client_slug: client.slug,
            service_id: service.id,
            ticket_id: ticket.id
        });

        try {
            const {
                error: confirmError,
                paymentIntent: confirmedPaymentIntent
            } = await stripe.confirmPayment({
                elements,
                confirmParams: {
                    return_url: [
                        window.location.origin,
                        returnUrl,
                        '?',
                        queryParams.toString()
                    ].join('')
                },
                redirect: 'if_required'
            });

            if (confirmError) {
                console.error('Error confirming bank payment', confirmError);
                throw new Error(
                    confirmError.message ||
                        'There was a problem confirming your payment. Please try again.'
                );
            }

            if (!confirmedPaymentIntent) {
                console.error('no response from confirmPayment');
                throw new Error(
                    'There was a problem confirming your payment. Please refresh the page and try again.'
                );
            }

            // if the payment request has been handled on a mobile
            // device it doesn't automatically redirect
            const shouldHandleRedirect = [
                'requires_payment_method',
                'succeeded',
                'processing'
            ].includes(confirmedPaymentIntent?.status);

            if (shouldHandleRedirect) {
                queryParams.set('payment_intent', confirmedPaymentIntent.id);
                queryParams.set(
                    'payment_intent_client_secret',
                    confirmedPaymentIntent.client_secret
                );

                history.push([returnUrl, queryParams.toString()].join('?'));
                window.scrollTo(0, 0);
            }
        } catch (e) {
            console.error(e);
            const errorMsg = getFriendlyPaymentError(e, DEFAULT_ERROR);
            setError(errorMsg);
            setPaymentError(!isSafePaymentError(errorMsg));
        } finally {
            setProcessing(false);
        }
    };

    const handleInitialPayment = async (imageFileName, imageUploadError) => {
        const currentSession = await Auth.currentSession();

        const headers = {
            Authorization: currentSession.getIdToken().getJwtToken()
        };

        const body = {
            payment_intent_id: paymentIntent.id,
            confirm: true,
            ticket,
            voucher,
            service,
            passenger: {
                ...passenger,
                ...(passenger.travelPattern && {
                    travel_pattern: passenger.travelPattern
                })
            },
            client,
            ...(imageUploadError && {
                skip_photo: true,
                image_upload_error: imageUploadError
            }),
            browser_info: {
                connection: connectionInfo(),
                battery: await batteryInfo()
            }
        };

        if (!disablePhoto) {
            body.passenger.photo = buyAgain ? passenger.photo : imageFileName;
        }

        const response = await axios.post(
            `${process.env.REACT_APP_PAYMENT_API_ENDPOINT}/customer-ticket/bank-direct-payment-intent`,
            body,
            {
                headers
            }
        );

        if (response.data.id && !response.data.requires_action) {
            // Payment complete (e.g., full voucher value, zero amount)
            const ticketYear = ticket.year.replace('/', '-');

            let confirmUrl = `/order-confirmed/${response.data.id}/${ticketYear}/${client.slug}/${service.id}/${ticket.id}`;

            if (isAdhoc(ticket)) {
                confirmUrl = `/order-confirmed/adhoc/${response.data.id}/${ticketYear}/${client.id}/${client.slug}/${service.id}/${ticket.id}`;
            }

            if (ticket.sub_type === 'fixed') {
                confirmUrl += '?is_fixed=true';
            }

            history.push(confirmUrl);
            window.scrollTo(0, 0);
        } else if (
            response.data.requires_action &&
            response.data.encrypted_ctid
        ) {
            setClientSideFlowStarted(true);
            setEncryptedTicketId(response.data.encrypted_ctid);
            setAllowEdit(false);

            // Initiate bank payment client-side flow
            await confirmPayment(response.data.encrypted_ctid);
        } else {
            throw new Error('Problem initiating pay by bank payment.');
        }
    };

    const handlePaymentElementChange = event => {
        if (Number(netPrice()) === 0) {
            // For zero value payments, we can skip the payment element validation and just enable the pay button
            setPaymentReady(true);
            return;
        }
        setPaymentReady(event.complete);
    };

    const handlePaymentElementLoadError = loadErr => {
        console.error('PaymentElement load error', loadErr);
        setError(
            'There was a problem loading the payment form. Please refresh the page and try again.'
        );
    };

    const handleSubmit = async event => {
        event.preventDefault();

        if (!stripe || !elements) {
            // Stripe.js has not loaded yet
            return;
        }

        if (clientSideFlowStarted) {
            console.log(
                'client side flow previously started, attempt to confirm payment again.'
            );

            setProcessing(true);
            setError('');
            await confirmPayment(encryptedTicketId);
            return;
        }

        // Validate minimum payment amount
        if (Number(netPrice()) > 0 && Number(netPrice()) < 1) {
            setError(
                `Invalid amount. Amount can only be 0 or a minimum of ${formatPrice(
                    1,
                    ticket.currency
                )} using pay by bank.`
            );
            return;
        }

        let imageFileName;
        let imageUploadError;

        setProcessing(true);
        setError('');

        if (!buyAgain && !disablePhoto) {
            try {
                imageFileName = await uploadImage();
            } catch (e) {
                console.error(e);

                const commonDetails = {
                    connection: connectionInfo(),
                    battery: await batteryInfo(),
                    ...(e.source && { source: e.source }),
                    ...(e.url && { url: e.url }),
                    ...(e.timing && { timing: e.timing })
                };

                if (e.response) {
                    imageUploadError = {
                        type: 'response',
                        status: e.response.status,
                        data: e.response.data,
                        ...commonDetails
                    };
                } else if (e.request) {
                    imageUploadError = {
                        type: 'request',
                        ready_state: e.request.readyState,
                        status: e.request.status,
                        timeout: e.request.timeout,
                        ...commonDetails
                    };
                } else {
                    imageUploadError = {
                        type: 'unknown',
                        message: e.message,
                        ...commonDetails
                    };
                }
            }
        }

        try {
            await handleInitialPayment(imageFileName, imageUploadError);
        } catch (e) {
            console.error(e);
            const errorMsg = getFriendlyPaymentError(e, DEFAULT_ERROR);
            setProcessing(false);
            setError(errorMsg);
            setPaymentError(!isSafePaymentError(errorMsg));
        }
    };

    const disablePayBtn =
        ((!stripe || !elements || !paymentReady) && Number(netPrice()) > 0) ||
        processing ||
        paymentError;

    return (
        <div className={classes.root}>
            <Typography variant="body1" style={{ paddingBottom: '1em' }}>
                By completing this purchase, I confirm I have read and accepted
                the following:
            </Typography>
            {service.terms_url && (
                <Typography variant="body1">
                    <a
                        href={service.terms_url}
                        rel="noopener noreferrer"
                        target="_blank"
                        style={{ color: '#000' }}
                    >
                        {client.name} T&amp;Cs
                    </a>
                </Typography>
            )}
            <Typography variant="body1">
                <a
                    href="https://shuttleid.uk/static/shuttleid-terms-of-use-passengers,-purchasers-and-other-users-updated-august-2020-8c80ca7b2c819e755e149871e5a98e88.pdf"
                    rel="noopener noreferrer"
                    target="_blank"
                    style={{ color: '#000' }}
                >
                    ShuttleID T&amp;Cs
                </a>
            </Typography>

            {netPrice() >= 1 && (
                <>
                    <div className={classes.paymentElement}>
                        <PaymentElement
                            onChange={handlePaymentElementChange}
                            onLoadError={handlePaymentElementLoadError}
                        />
                    </div>
                    <Typography variant="body1">
                        I understand I have{' '}
                        <a
                            href="https://help.shuttleid.uk/en/articles/14021821-pay-by-bank-booking-reservation"
                            rel="noopener noreferrer"
                            target="_blank"
                            style={{ color: '#000', fontWeight: 'bold' }}
                        >
                            15 minutes to confirm payment
                        </a>{' '}
                        via my bank
                    </Typography>
                </>
            )}

            {error && (
                <Typography className={classes.error} variant="body2">
                    {error}
                </Typography>
            )}

            <LoaderButton
                className={`${classes.button} pay-btn`}
                variant="outlined"
                color="primary"
                type="submit"
                isLoading={processing}
                disabled={disablePayBtn}
                text={`Confirm & pay ${formatPrice(
                    netPrice(),
                    ticket.currency
                )}`}
                loadingText="Processing..."
                onClick={handleSubmit}
            />
        </div>
    );
};

BankPaymentForm.propTypes = {
    classes: PropTypes.shape().isRequired,
    history: PropTypes.shape().isRequired,
    ticket: PropTypes.shape().isRequired,
    service: PropTypes.shape().isRequired,
    passenger: PropTypes.shape().isRequired,
    client: PropTypes.shape().isRequired,
    voucher: PropTypes.shape(),
    buyAgain: PropTypes.bool,
    disablePhoto: PropTypes.bool,
    paymentIntent: PropTypes.shape().isRequired,
    setAllowEdit: PropTypes.func.isRequired
};

BankPaymentForm.defaultProps = {
    voucher: null,
    buyAgain: false,
    disablePhoto: false
};

export default withStyles(styles)(withRouter(withWidth()(BankPaymentForm)));
